import axios from "axios";

export const API_ROOT = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_ROOT,
});

const TOKEN_KEY = "lldls_token";
// Clés legacy à purger opportunément si présentes dans localStorage
// (héritage renommage marque LLDLS → Wérko).
const LEGACY_TOKEN_KEYS = ["werko_token"];

// Stratégie 1 C1A v2 (15/09/2026) — Isolation stricte par onglet.
//
// Défauts corrigés :
//   A. `setToken` n'écrit plus dans localStorage (plus de pollution
//      globale à chaque login).
//   B. `getToken` ne lit plus localStorage en fallback permanent : il
//      effectue une MIGRATION DESTRUCTIVE one-shot au premier appel d'un
//      onglet (sessionStorage vide + localStorage hérité) — le token est
//      déplacé vers sessionStorage puis effacé de localStorage. Un seul
//      onglet peut donc hériter d'une session pré-déploiement, aucun
//      nouvel onglet ne peut ensuite piocher dans localStorage.
//   C. Purge défensive des clés legacy (`werko_token`) à chaque écriture
//      pour éviter tout résidu.
//
// Trade-off assumé : sessionStorage est volatil. La fermeture complète
// du navigateur vide sessionStorage → l'utilisateur devra se reconnecter
// au prochain démarrage. La roadmap "slots multi-société natifs"
// (stratégie 3, consignée BACKLOG) restaurera la persistance de session
// tout en préservant l'isolation.
function purgeLegacy() {
  try {
    LEGACY_TOKEN_KEYS.forEach((k) => window.localStorage.removeItem(k));
  } catch {
    /* ignore */
  }
}

export function getToken() {
  try {
    const s = window.sessionStorage.getItem(TOKEN_KEY);
    if (s) return s;
    // Migration one-shot destructive depuis localStorage hérité
    // (utilisateurs déjà connectés avant ce déploiement).
    const l = window.localStorage.getItem(TOKEN_KEY);
    if (l) {
      window.sessionStorage.setItem(TOKEN_KEY, l);
      window.localStorage.removeItem(TOKEN_KEY);
      purgeLegacy();
      return l;
    }
    return null;
  } catch {
    return null;
  }
}

export function setToken(t) {
  try {
    if (t) {
      window.sessionStorage.setItem(TOKEN_KEY, t);
      // On n'écrit PLUS dans localStorage (défaut A du diagnostic C1A).
      // Purge défensive au cas où un onglet précédent aurait laissé
      // un héritage `lldls_token` ou une clé legacy.
      window.localStorage.removeItem(TOKEN_KEY);
      purgeLegacy();
    } else {
      window.sessionStorage.removeItem(TOKEN_KEY);
      window.localStorage.removeItem(TOKEN_KEY);
      purgeLegacy();
    }
  } catch {
    /* ignore */
  }
}

api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Bloc 11/10 09h20 — Intercepteur response : plus jamais de
// « Request failed with status code 500 » dans l'UI.
//
// Toute erreur HTTP 5xx est remplacée par un message français humain,
// contextualisé par méthode + ressource si possible. Le détail technique
// (status, URL, response.data) reste dans la console pour support/debug.
// Les erreurs 4xx sont laissées telles quelles (déjà exploitées par
// `humanizeApiError` + `formatApiError` qui lisent le `detail` serveur).
//
// L'erreur rejetée est RÉ-ENRICHIE : `err.response.data.detail` est écrasée
// par le message FR → les call-sites existants (`toast.error(formatApiError(e))`)
// afficheront automatiquement le message FR.
function _buildFrServerErrorMessage(cfg) {
  const url = cfg?.url || "";
  // Mapping par préfixe de route — ajoute ici au besoin.
  const map = [
    { re: /\/chantiers\b/, msg: "Impossible de charger les chantiers. Réessayez dans un instant." },
    { re: /\/invoices\b/, msg: "Impossible de charger les factures. Réessayez dans un instant." },
    { re: /\/quotes\b/, msg: "Impossible de charger les devis. Réessayez dans un instant." },
    { re: /\/contacts\b/, msg: "Impossible de charger les contacts. Réessayez dans un instant." },
    { re: /\/leads\b/, msg: "Impossible de charger les demandes. Réessayez dans un instant." },
    { re: /\/credit[-_]notes\b/, msg: "Impossible de charger les notes de crédit. Réessayez dans un instant." },
    { re: /\/pdf\/sign\b/, msg: "Impossible de générer le PDF pour l'instant. Réessayez dans un instant." },
    { re: /\/rendezvous\b/, msg: "Impossible de charger les rendez-vous. Réessayez dans un instant." },
  ];
  for (const { re, msg } of map) {
    if (re.test(url)) return msg;
  }
  return "Une erreur est survenue côté serveur. Réessayez dans un instant.";
}

api.interceptors.response.use(
  (resp) => resp,
  (error) => {
    const status = error?.response?.status;
    if (status && status >= 500 && status < 600) {
      // Trace technique complète en console pour support (jamais à l'écran).
      // eslint-disable-next-line no-console
      console.error("[API 5xx]", {
        status,
        method: error.config?.method,
        url: error.config?.url,
        detail: error.response?.data,
      });
      const frMessage = _buildFrServerErrorMessage(error.config);
      error.response.data = {
        ...(error.response.data || {}),
        detail: frMessage,
      };
    }
    return Promise.reject(error);
  }
);

export function formatApiError(err) {
  const d = err?.response?.data?.detail;
  if (d == null) return err?.message || "Erreur inconnue";
  if (typeof d === "string") return d;
  if (Array.isArray(d))
    return d.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" · ");
  if (typeof d === "object") {
    // Item 1-fix (2026-09-20) — Garde-fou "jamais [object Object]" quand le
    // backend renvoie `detail` sous forme d'objet (Pydantic → `msg`, ancien
    // rate_limit → `error`, conventions internes → `message`). Fallback
    // JSON pour ne pas perdre l'info tout en restant lisible.
    return d.msg || d.error || d.message || JSON.stringify(d);
  }
  return String(d);
}

/**
 * Commit 2 hardening (2026-09-21) — Messages d'erreur adaptés au contexte.
 *
 * Deux fonctions distinctes selon l'audience :
 *
 *   humanizeApiError → pages PRIVÉES (pro connecté).
 *     Retourne le message backend s'il existe (via formatApiError),
 *     sinon le fallback métier. Le pro voit un détail exploitable
 *     (ex: "Timeout Whisper", "Quota IA épuisé") — utile en démo /
 *     support pour comprendre la nature de l'échec.
 *
 *   publicApiError → pages PUBLIQUES (client final non identifié).
 *     Retourne TOUJOURS le fallback métier, jamais le detail backend.
 *     Détail technique logué en console (traçabilité debug support
 *     via devtools) mais jamais affiché à l'écran. Un particulier
 *     qui signe un devis ne doit pas voir "IntegrityError on quotes.status".
 *
 * Convention `user_facing` (backlog CONFORT — pas encore implémentée
 * côté backend) : quand un endpoint public voudra faire remonter un
 * message spécifique au client final, il devra retourner
 * `{"detail": "...", "user_facing": true}`. `publicApiError` détectera
 * ce flag et utilisera le detail au lieu du fallback. Aujourd'hui aucun
 * endpoint ne l'utilise → comportement toujours = fallback.
 */
export function humanizeApiError(err, fallback = "Une erreur est survenue. Réessayez.") {
  // Un vrai message backend arrive via `err.response.data.detail`. Les
  // erreurs bas-niveau axios (`err.message = "Network Error"`, "timeout of
  // 30000ms exceeded") ne sont PAS des messages métier — on préfère le
  // fallback contextuel plutôt que de les exposer tels quels au pro.
  const d = err?.response?.data?.detail;
  if (d == null) return fallback;
  if (typeof d === "string") return d;
  if (Array.isArray(d))
    return d.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" · ") || fallback;
  if (typeof d === "object") {
    return d.msg || d.error || d.message || fallback;
  }
  return String(d) || fallback;
}

export function publicApiError(err, fallback = "Une erreur est survenue. Réessayez.") {
  // Trace debug pour support (jamais à l'écran).
  console.error("[Public API error]", err);
  // Marquage explicite par le backend → autorisé à remonter à l'écran.
  const d = err?.response?.data;
  if (d && typeof d === "object" && d.user_facing === true && typeof d.detail === "string") {
    return d.detail;
  }
  return fallback;
}
